import React, { useEffect, useState, useRef, lazy, createElement, Component } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import { Heart, Sparkles, Wind, Moon, Sun, Check, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ArrowRight, Flower2, Leaf, ShieldCheck, Smartphone, Download, Star } from 'lucide-react';
// --- Components ---
const FloatingPetals = () => {
  return <div className="absolute inset-0 pointer-events-none overflow-hidden z-20" data-id="element-0">
      {[...Array(10)].map((_, i) => <motion.div key={i} initial={{
      x: Math.random() * 100 + '%',
      y: -50,
      opacity: 0,
      rotate: 0,
      scale: 0.5 + Math.random() * 0.5
    }} animate={{
      y: '120%',
      opacity: [0, 0.6, 0.6, 0],
      rotate: [0, 180, 360],
      x: [(Math.random() * 100).toString() + '%', (Math.random() * 100 + (Math.random() > 0.5 ? 10 : -10)).toString() + '%', (Math.random() * 100).toString() + '%']
    }} transition={{
      duration: 20 + Math.random() * 15,
      repeat: Infinity,
      delay: Math.random() * 25,
      ease: 'easeInOut'
    }} className="absolute w-4 h-4 bg-[#FCE4EC]/60 rounded-full blur-[0.5px]" style={{
      borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%'
    }} data-id="element-1" />)}
    </div>;
};
const LightParticles = () => {
  return <div className="absolute inset-0 pointer-events-none z-10" data-id="element-2">
      {[...Array(12)].map((_, i) => <motion.div key={i} initial={{
      x: Math.random() * 100 + '%',
      y: Math.random() * 100 + '%',
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: [0, 0.4, 0],
      scale: [0, 1.2, 0],
      y: [null, '-=20']
    }} transition={{
      duration: 6 + Math.random() * 6,
      repeat: Infinity,
      delay: Math.random() * 12,
      ease: 'easeInOut'
    }} className="absolute w-1.5 h-1.5 bg-[#D4AF37]/40 rounded-full blur-[1.5px]" data-id="element-3" />)}
    </div>;
};
const EtherealBreeze = () => {
  return <div className="absolute inset-0 pointer-events-none overflow-hidden z-10" data-id="element-4">
      <motion.div animate={{
      x: ['-150%', '150%'],
      opacity: [0, 0.08, 0]
    }} transition={{
      duration: 25,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute top-1/4 left-0 w-full h-64 bg-gradient-to-r from-transparent via-white/30 to-transparent blur-[100px] rotate-[-15deg]" data-id="element-5" />
      <motion.div animate={{
      x: ['150%', '-150%'],
      opacity: [0, 0.05, 0]
    }} transition={{
      duration: 30,
      repeat: Infinity,
      ease: 'linear',
      delay: 5
    }} className="absolute bottom-1/4 left-0 w-full h-48 bg-gradient-to-r from-transparent via-[#FCE4EC]/20 to-transparent blur-[80px] rotate-[10deg]" data-id="element-6" />
    </div>;
};
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-8 md:py-12 px-6 ${className}`} data-id="element-7">
    <div className="max-w-5xl mx-auto" data-id="element-8">{children}</div>
  </section>;
const Button = ({
  children,
  onClick,
  className = '',
  variant = 'primary'
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: 'primary' | 'secondary';
}) => {
  const baseStyles = 'box-shadow: px-8 py-4 rounded-full font-semibold transition-all duration-300 inline-block cursor-pointer shadow-sm hover:shadow-md active:scale-95';
  const variants = {
    primary: 'bg-[#E1BEE7] text-[#4E342E] hover:bg-[#D1A3D1]',
    secondary: 'bg-white border border-[#E1BEE7] text-[#4E342E] hover:bg-[#FDF7FF]'
  };
  return <motion.button whileHover={{
    y: -2
  }} whileTap={{
    scale: 0.98
  }} onClick={onClick} className={cn(`${baseStyles} ${variants[variant]} ${className}`, 'text-[16px] pt-[10px] pb-[10px] pl-[0px] pr-[0px]', 'text-[#FFFFFF] bg-[#51B228]')} data-id="element-9">
      {children}
    </motion.button>;
};
const PlaceholderImage = ({
  label,
  className = 'aspect-video'
}: {
  label: string;
  className?: string;
}) => <div className={`w-full bg-[#F9F5F0] border-2 border-dashed border-[#E1BEE7]/30 rounded-2xl flex items-center justify-center p-8 text-center text-[#8D6E63] italic font-light ${className}`} data-id="element-10">
    <div className="flex flex-col items-center gap-3" data-id="element-11">
      <Sparkles className="w-6 h-6 opacity-30" data-id="element-12" />
      <span data-id="element-13">{label}</span>
    </div>
  </div>;
const ImageCarousel = ({
  images,
  className = ''
}: {
  images: string[];
  className?: string;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % images.length);
    }, 60000); // 1 minute
    return () => clearInterval(timer);
  }, [images.length]);
  return <div className={`relative overflow-hidden rounded-full aspect-square ${className}`} data-id="element-14">
      <AnimatePresence mode="wait" data-id="element-15">
        <motion.img key={currentIndex} src={images[currentIndex]} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1.5,
        ease: 'easeInOut'
      }} className="absolute inset-0 w-full h-full object-cover" data-id="element-16" />
      </AnimatePresence>
    </div>;
};
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-[#E1BEE7]/30 py-4" data-id="element-17">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center text-left gap-4 group" data-id="element-18">
        <span className="text-lg font-medium text-[#4E342E] group-hover:text-[#8D6E63] transition-colors" data-id="element-19">
          {question}
        </span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-[#8D6E63]" data-id="element-20" /> : <ChevronDown className="w-5 h-5 text-[#8D6E63]" data-id="element-21" />}
      </button>
      <AnimatePresence data-id="element-22">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-23">
            <p className="pt-4 pb-2 text-[#6D4C41] leading-relaxed" data-id="element-24">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const FloatingBar = () => {
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [showNotification, setShowNotification] = useState(false);
  const [buyerName, setBuyerName] = useState('María');
  const names = ['María', 'Laura', 'Sofía', 'Ana', 'Valentina', 'Camila', 'Martina', 'Lucía', 'Florencia', 'Julieta', 'Carolina', 'Agustina', 'Paula', 'Daniela', 'Mariana'];
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const notifTimer = setInterval(() => {
      setBuyerName(names[Math.floor(Math.random() * names.length)]);
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 5000); // Se muestra por 5 segundos
    }, 20000); // Aparece cada 20 segundos
    return () => clearInterval(notifTimer);
  }, []);
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return <div className="fixed bottom-0 left-0 w-full z-50 flex flex-col items-center pointer-events-none" data-id="element-25">
      <AnimatePresence data-id="element-26">
        {showNotification && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 10
      }} className="bg-white text-[#4E342E] px-4 py-2 rounded-t-xl shadow-[0_-10px_30px_rgba(0,0,0,0.1)] text-[11px] md:text-xs font-medium border border-[#E1BEE7]/50 border-b-0 flex items-center gap-2 pointer-events-auto mb-[-1px]" data-id="element-27">
            <div className="relative flex h-2 w-2" data-id="element-28">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#51B228] opacity-75" data-id="element-29"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#51B228]" data-id="element-30"></span>
            </div>
            <span data-id="element-31">
              <strong data-id="element-32">{buyerName}</strong> acaba de ingresar a Volver a Mí
            </span>
          </motion.div>}
      </AnimatePresence>
      <div className="w-full bg-gradient-to-r from-[#4E342E] to-[#6D4C41] text-white py-2 px-2 md:px-4 shadow-[0_-10px_40px_rgba(0,0,0,0.2)] flex flex-row flex-wrap items-center justify-center gap-x-3 gap-y-1.5 pointer-events-auto border-t border-white/10" data-id="element-33">
        <div className="flex items-center gap-1.5 font-bold text-[11px] md:text-sm tracking-wide" data-id="element-34">
          <Sparkles className="w-3 h-3 text-[#D4AF37]" data-id="element-35" />
          <span className="text-[#D4AF37]" data-id="element-36">50% OFF</span>
        </div>

        <div className="flex items-center gap-1 font-mono font-bold text-xs md:text-sm" data-id="element-37">
          <div className="bg-white/10 px-1.5 py-0.5 rounded backdrop-blur-sm border border-white/10" data-id="element-38">
            {minutes.toString().padStart(2, '0')}
          </div>
          <span className="opacity-70" data-id="element-39">:</span>
          <div className="bg-white/10 px-1.5 py-0.5 rounded backdrop-blur-sm border border-white/10" data-id="element-40">
            {seconds.toString().padStart(2, '0')}
          </div>
        </div>

        <button onClick={() => document.getElementById('oferta')?.scrollIntoView({
        behavior: 'smooth'
      })} className="bg-[#E1BEE7] text-[#4E342E] text-[10px] md:text-xs font-bold px-3 py-1 md:py-1.5 rounded-full hover:bg-[#D1A3D1] transition-colors ml-1 md:ml-4 shadow-sm active:scale-95" data-id="element-41">
          Ver oferta
        </button>
      </div>
    </div>;
};
const HooponoponoCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const cards = [{
    id: 1,
    title: 'Suelto y libero',
    image: 'https://cdn.magicpatterns.com/uploads/kZZ5HJbv65XjQE8SjrNw1M/ChatGPT_Image_24_jun_2026,_16_49_35.png'
  }, {
    id: 2,
    title: 'Vuelvo a la paz',
    image: 'https://cdn.magicpatterns.com/uploads/8DdbgYdem3MNY7ohUCBrUT/ChatGPT_Image_24_jun_2026,_16_49_39.png'
  }, {
    id: 3,
    title: 'Me elijo con amor',
    image: 'https://cdn.magicpatterns.com/uploads/qGssMhJCsQgHvtxb67i6Kk/ChatGPT_Image_24_jun_2026,_16_49_53.png'
  }];
  const nextCard = () => {
    setCurrentIndex(prev => prev === cards.length - 1 ? 0 : prev + 1);
  };
  const prevCard = () => {
    setCurrentIndex(prev => prev === 0 ? cards.length - 1 : prev - 1);
  };
  // Swipe handling
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      nextCard();
    }
    if (isRightSwipe) {
      prevCard();
    }
  };
  return <Section className="bg-[#FFFDFB]" data-id="element-42">
      <div className="text-center mb-10" data-id="element-43">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#4E342E]" data-id="element-44">
          3 cartas de Ho’oponopono para acompañar tu proceso
        </h2>
        <p className="text-lg text-[#6D4C41] font-light max-w-2xl mx-auto" data-id="element-45">
          Deslizá y elegí la carta que necesites hoy: soltar, volver a la calma
          o reconectar con tu amor propio.
        </p>
      </div>

      <div className="relative max-w-md mx-auto w-[94%] md:w-full" data-id="element-46">
        <div className="overflow-hidden rounded-3xl shadow-[0_10px_40px_-10px_rgba(225,190,231,0.4)] border border-[#E1BEE7]/30 bg-white relative" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} data-id="element-47">
          <div className="relative aspect-[4/5] w-full" data-id="element-48">
            <AnimatePresence mode="wait" data-id="element-49">
              <motion.img key={currentIndex} src={cards[currentIndex].image} alt={cards[currentIndex].title} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="absolute inset-0 w-full h-full object-contain p-2" data-id="element-50" />
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button onClick={prevCard} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md text-[#8D6E63] hover:text-[#4E342E] hover:bg-white transition-all z-10" aria-label="Carta anterior" data-id="element-51">
            <ChevronLeft className="w-6 h-6" data-id="element-52" />
          </button>
          <button onClick={nextCard} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md text-[#8D6E63] hover:text-[#4E342E] hover:bg-white transition-all z-10" aria-label="Siguiente carta" data-id="element-53">
            <ChevronRight className="w-6 h-6" data-id="element-54" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 mt-6" data-id="element-55">
          {cards.map((_, idx) => <button key={idx} onClick={() => setCurrentIndex(idx)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${currentIndex === idx ? 'bg-[#D4AF37] w-6' : 'bg-[#E1BEE7]/50 hover:bg-[#E1BEE7]'}`} aria-label={`Ir a carta ${idx + 1}`} data-id="element-56" />)}
        </div>
      </div>

      <p className="text-center mt-8 text-sm text-[#8D6E63] italic font-light" data-id="element-57">
        Podés guardar la carta que más necesites y repetir su mensaje en un
        momento de calma.
      </p>
    </Section>;
};
// --- Main Application ---
export function App() {
  useEffect(() => {
    // Meta Pixel Code
    const w = window as any;
    if (!w.fbq) {
      const n: any = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      w.fbq = n;
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const s = document.getElementsByTagName('script')[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        document.head.appendChild(t);
      }
    }
    w.fbq('init', '1488332819199981');
    w.fbq('track', 'PageView');
  }, []);
  const [experienceForm, setExperienceForm] = useState({
    name: '',
    location: '',
    comment: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [previewData, setPreviewData] = useState<null | {
    name: string;
    location: string;
    comment: string;
  }>(null);
  const handleExperienceSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!experienceForm.name || !experienceForm.location || !experienceForm.comment) return;
    setPreviewData({
      ...experienceForm
    });
    setIsSubmitted(true);
    setExperienceForm({
      name: '',
      location: '',
      comment: ''
    });
  };
  const heroRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });
  const yParallax = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacityParallax = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scaleParallax = useTransform(scrollYProgress, [0, 1], [1, 1.05]);
  const scrollToOffer = () => {
    document.getElementById('oferta')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="selection:bg-#[FCE4EC] selection:text-#[4E342E]" data-id="element-58">
      <FloatingBar data-id="element-59" />
      {/* HERO PRINCIPAL */}
      <section ref={heroRef} className="relative min-h-90vh md:min-h-screen flex flex-col items-center justify-center pt-20 pb-16 px-0 md:px-6 overflow-hidden bg-gradient-to-b from-#[FFFDFB] via-#[FFFDFB] to-#[F9F5F0]/30" data-id="element-60">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden" data-id="element-61">
          <motion.div style={{
          y: yParallax,
          opacity: opacityParallax
        }} className="absolute -top-20 -left-20 w-[40vw] h-[40vw] bg-[#FCE4EC]/30 blur-[120px] rounded-full" data-id="element-62" />
          <motion.div style={{
          y: useTransform(scrollYProgress, [0, 1], [0, -100])
        }} className="absolute bottom-0 -right-20 w-[50vw] h-[50vw] bg-[#F3E5F5]/20 blur-[150px] rounded-full" data-id="element-63" />
        </div>

        <div className="relative z-30 max-w-4xl w-full mx-auto text-center flex flex-col items-center" data-id="element-64">
          {/* Badge */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex items-center gap-2 text-[#D4AF37] mb-8" data-id="element-65">
            <Flower2 className="w-5 h-5" data-id="element-66" />
            <span className="uppercase tracking-[0.3em] text-[10px] md:text-xs font-bold" data-id="element-67">
              Guía de Sanación & Amor Propio
            </span>
          </motion.div>

          {/* Main Hero Image Container */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.5,
          ease: [0.22, 1, 0.36, 1]
        }} style={{
          scale: scaleParallax
        }} className="relative w-full md:max-w-[500px] mb-12 group" data-id="element-68">
            {/* Soft Glow behind image */}
            <div className="absolute -inset-10 bg-gradient-to-tr from-[#FCE4EC]/50 to-[#F3E5F5]/50 blur-[80px] rounded-full -z-10 animate-pulse-soft hidden md:block" data-id="element-69" />

            {/* The Image */}
            <motion.div animate={{
            y: [0, -15, 0]
          }} transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative rounded-none md:rounded-#[3rem] overflow-hidden shadow-#[0_30px_100px_-20px_rgba(78,52,46,0.15)] md:border border-#[E1BEE7]/40 bg-white" data-id="element-70">
              <img src="https://cdn.magicpatterns.com/uploads/2vKHsdGcpuBQeav2whTUhN/ChatGPT_Image_12_jun_2026,_14_50_53.webp" alt="Volver a Mí - Portada Principal" className="w-full h-auto object-cover scale-[1.01]" loading="eager" data-id="element-71" />

              {/* Animation Overlays */}
              <FloatingPetals data-id="element-72" />
              <LightParticles data-id="element-73" />
              <EtherealBreeze data-id="element-74" />

              {/* Candle Flicker Overlay - Positioned over the candle in the image */}
              <motion.div animate={{
              opacity: [0.15, 0.35, 0.15],
              scale: [1, 1.15, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute bottom-[10%] left-[8%] w-20 h-20 bg-[#D4AF37]/25 blur-3xl rounded-full pointer-events-none" data-id="element-75" />
            </motion.div>
          </motion.div>

          {/* Text Content - Positioned below the image for better flow on mobile */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.4
        }} className="max-w-2xl px-6 md:px-0" data-id="element-76">
            <p className="text-lg md:text-xl text-[#6D4C41] leading-relaxed mb-10 font-light italic" data-id="element-77">
              "Volver a Mí es el camino para soltar lo que pesa, cerrar ciclos y
              reconectar con tu verdadera esencia desde el amor."
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-78">
              <Button onClick={scrollToOffer} className="w-full sm:w-auto px-16 py-5 text-lg shadow-xl shadow-[#E1BEE7]/20" data-id="element-79">
                Quiero empezar hoy
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-4 opacity-70" data-id="element-80">
              {['Soltá cargas', 'Cerrá ciclos', 'Calmá tu mente', 'Reconectá con vos'].map((item, i) => <div key={i} className="flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-widest font-bold text-[#8D6E63]" data-id="element-81">
                  <Sparkles className="w-3 h-3 text-[#D4AF37]" data-id="element-82" />
                  {item}
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Bottom Fade */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#F9F5F0]/30 to-transparent pointer-events-none" data-id="element-83" />
      </section>

      {/* BLOQUE DE DOLOR EMOCIONAL */}
      <Section className="bg-[#F9F5F0]/50" data-id="element-84">
        <div className="max-w-3xl mx-auto text-center" data-id="element-85">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-86">
            <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-87">
              Hay momentos en los que una mujer necesita volver a sí misma
            </h2>
            <div className="space-y-6 text-lg text-[#6D4C41] font-light leading-relaxed" data-id="element-88">
              <p data-id="element-89">
                Tal vez venís cargando emociones, recuerdos, vínculos o
                pensamientos que ya no querés seguir sosteniendo. Tal vez
                intentaste avanzar, pero algo adentro todavía pide calma, cierre
                y amor.
              </p>
              <p data-id="element-90">
                Volver a Mí fue creado para acompañarte en ese proceso: soltar
                lo que pesa, ordenar lo que sentís y abrir espacio para una
                etapa más liviana, consciente y en paz.
              </p>
            </div>

            <div className="mt-12 p-8 bg-white rounded-3xl border border-[#E1BEE7]/20 shadow-sm italic text-xl text-[#4E342E]" data-id="element-91">
              "No se trata de hacer más. Se trata de soltar lo que pesa y volver
              a vos."
            </div>
          </motion.div>
        </div>
      </Section>

      {/* BLOQUE ESTO ES PARA VOS SI... */}
      <Section data-id="element-92">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-93">
          <div className="order-2 md:order-1" data-id="element-94">
            <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-95">
              Este material es para vos si querés…
            </h2>
            <ul className="space-y-5" data-id="element-96">
              {['Soltar emociones que venís guardando hace tiempo.', 'Cerrar ciclos sin quedarte atrapada en el dolor.', 'Calmar tu mente cuando sentís que no se detiene.', 'Reconectar con tu amor propio y volver a priorizarte.', 'Crear una rutina espiritual simple, femenina y amorosa.', 'Sentirte más liviana, presente y en paz.', 'Acompañar tu proceso interior con ejercicios prácticos.', 'Empezar una nueva etapa desde adentro.'].map((item, i) => <li key={i} className="flex items-center gap-4 text-[#5D4037] text-lg" data-id="element-97">
                  <Sparkles className="w-5 h-5 text-[#D4AF37] shrink-0" data-id="element-98" />
                  <span data-id="element-99">{item}</span>
                </li>)}
            </ul>
          </div>
          <div className="order-1 md:order-2" data-id="element-100">
            <ImageCarousel images={['https://cdn.magicpatterns.com/uploads/aMh5mmzoSHa5NL4EnRbqF2/ChatGPT_Image_12_jun_2026,_16_31_27.webp', 'https://cdn.magicpatterns.com/uploads/nxrjA97uVzSURhzdhPi6vx/ChatGPT_Image_12_jun_2026,_16_31_32.webp', 'https://cdn.magicpatterns.com/uploads/nmbPr5T16i1ZwfZ8pYZozP/ChatGPT_Image_12_jun_2026,_16_31_36.webp']} className="shadow-2xl border-4 border-white" data-id="element-101" />
          </div>
        </div>
      </Section>

      {/* PRESENTACIÓN DEL PRODUCTO */}
      <Section className="bg-[#FDF7FF]" data-id="element-102">
        <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-103">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="relative" data-id="element-104">
            <div className="absolute -inset-4 bg-[#FCE4EC]/30 blur-3xl rounded-full -z-10" data-id="element-105" />
            <img src="https://cdn.magicpatterns.com/uploads/3XtRpgZ1MA9pnNrFpV6Khs/ChatGPT_Image_12_jun_2026,_16_48_54.webp" alt="Mujer en calma - Volver a Mí" className="w-full h-auto rounded-[2.5rem] shadow-2xl border border-white/20 object-cover aspect-[3/4]" data-id="element-106" />
          </motion.div>

          <div data-id="element-107">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-108">
              ¿Qué es Volver a Mí?
            </h2>
            <div className="space-y-6 text-lg text-[#6D4C41] font-light leading-relaxed" data-id="element-109">
              <p data-id="element-110">
                Volver a Mí es una guía práctica y espiritual creada para
                acompañarte en un proceso de sanación emocional, amor propio y
                paz interior.
              </p>
              <p data-id="element-111">
                Dentro vas a encontrar prácticas simples de Ho’oponopono,
                gratitud, respiración, mantras, chakras, escritura emocional y
                rituales de cierre para ayudarte a reconectar con vos misma.
              </p>
              <p data-id="element-112">
                No necesitás experiencia previa. Solo un momento para vos, una
                intención clara y el deseo de empezar a soltar.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* QUÉ ES EL HO'OPONOPONO */}
      <Section className="bg-[#F9F5F0]/80" data-id="element-113">
        <div className="max-w-5xl mx-auto" data-id="element-114">
          <div className="text-center mb-12 md:mb-16" data-id="element-115">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#4E342E]" data-id="element-116">
              ¿Qué es el Ho’oponopono?
            </h2>
            <p className="text-lg md:text-xl text-[#6D4C41] font-light leading-relaxed max-w-3xl mx-auto" data-id="element-117">
              El Ho’oponopono es una práctica hawaiana de reconciliación, perdón
              y limpieza interior. Su idea central es asumir responsabilidad por
              lo que sentimos y trabajar internamente para liberar recuerdos,
              emociones y conflictos que siguen generando dolor.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center mb-16 md:mb-24" data-id="element-118">
            <div className="order-2 md:order-1" data-id="element-119">
              <h3 className="text-2xl md:text-3xl font-bold mb-8 text-[#8D6E63]" data-id="element-120">
                Una práctica simple basada en cuatro frases
              </h3>

              <div className="bg-[#FCE4EC]/40 p-8 md:p-12 rounded-[2.5rem] md:rounded-[3rem] border border-[#D4AF37]/30 shadow-sm text-center relative overflow-hidden group" data-id="element-121">
                <div className="absolute top-0 right-0 p-4 opacity-20" data-id="element-122">
                  <Flower2 className="w-12 h-12 text-[#D4AF37]" data-id="element-123" />
                </div>
                <div className="space-y-4 md:space-y-6 relative z-10" data-id="element-124">
                  <p className="text-3xl md:text-5xl font-serif font-bold text-[#4E342E] italic" data-id="element-125">
                    Lo siento.
                  </p>
                  <p className="text-3xl md:text-5xl font-serif font-bold text-[#4E342E] italic" data-id="element-126">
                    Perdóname.
                  </p>
                  <p className="text-3xl md:text-5xl font-serif font-bold text-[#4E342E] italic" data-id="element-127">
                    Gracias.
                  </p>
                  <p className="text-3xl md:text-5xl font-serif font-bold text-[#4E342E] italic" data-id="element-128">
                    Te amo.
                  </p>
                </div>
              </div>

              <p className="mt-8 text-lg text-[#6D4C41] font-light leading-relaxed" data-id="element-129">
                Estas palabras se usan como una forma de calmar la mente, soltar
                cargas emocionales, sanar vínculos y volver a un estado de mayor
                paz. No se trata de negar lo vivido, sino de cambiar la relación
                que tenemos con ese recuerdo o situación.
              </p>
            </div>

            <div className="order-1 md:order-2" data-id="element-130">
              <img src="https://cdn.magicpatterns.com/uploads/61zCPjEQRq5vnFQrWi2J7Y/ChatGPT_Image_24_jun_2026,_17_55_45.png" alt="Práctica de Ho'oponopono" className="w-full h-full object-cover aspect-[4/5] md:aspect-[3/4] rounded-[3rem] shadow-xl border border-[#E1BEE7]/30" data-id="element-131" />
            </div>
          </div>

          <div className="bg-white p-8 md:p-16 rounded-[3rem] md:rounded-[4rem] border border-[#E1BEE7]/20 shadow-xl text-center" data-id="element-132">
            <h3 className="text-3xl md:text-4xl font-bold mb-10 text-[#4E342E]" data-id="element-133">
              ¿Cómo puede acompañarte?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 text-left max-w-3xl mx-auto mb-12" data-id="element-134">
              {['Soltar pensamientos y emociones repetitivas.', 'Cerrar ciclos.', 'Trabajar el perdón.', 'Recuperar calma.', 'Reconectar con el amor propio.'].map((item, i) => <div key={i} className="flex items-center gap-4 text-[#5D4037] text-lg" data-id="element-135">
                  <div className="w-12 h-12 bg-[#F9F5F0] rounded-full flex items-center justify-center shrink-0 shadow-sm border border-[#E1BEE7]/20" data-id="element-136">
                    <Leaf className="w-5 h-5 text-[#D4AF37]" data-id="element-137" />
                  </div>
                  <span className="font-medium" data-id="element-138">{item}</span>
                </div>)}
            </div>

            <div className="inline-block px-6 md:px-10 py-6 bg-[#FCE4EC]/30 rounded-3xl border border-[#E1BEE7]/30" data-id="element-139">
              <p className="text-xl md:text-2xl text-[#8D6E63] font-serif italic" data-id="element-140">
                "No se trata de olvidar lo vivido, sino de dejar de cargarlo de
                la misma manera."
              </p>
            </div>
          </div>

          <div className="text-center mt-16" data-id="element-141">
            <Button onClick={scrollToOffer} className="w-full sm:w-auto px-12 py-5 text-lg shadow-xl shadow-[#E1BEE7]/20" data-id="element-142">
              Conocer las prácticas de Volver a Mí
            </Button>
            <p className="mt-8 text-xs text-[#8D6E63] opacity-70 max-w-2xl mx-auto leading-relaxed" data-id="element-143">
              El Ho’oponopono es una práctica de bienestar espiritual y
              crecimiento personal. No reemplaza terapia, atención médica ni
              acompañamiento profesional.
            </p>
          </div>
        </div>
      </Section>

      {/* BLOQUE DE TRANSFORMACIÓN */}
      <Section data-id="element-144">
        <div className="text-center mb-16" data-id="element-145">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-146">
            De cargar en silencio a volver a sentirte en paz
          </h2>
          <p className="text-[#8D6E63] italic" data-id="element-147">
            Un camino de transformación consciente
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-148">
          <div className="bg-[#F9F5F0] p-10 rounded-[2rem] border border-[#E1BEE7]/10" data-id="element-149">
            <h3 className="text-2xl font-bold mb-8 text-[#8D6E63]" data-id="element-150">
              Antes de empezar este camino:
            </h3>
            <ul className="space-y-4" data-id="element-151">
              {['Mente llena.', 'Emociones acumuladas.', 'Vínculos que todavía pesan.', 'Falta de claridad interior.', 'Sensación de bloqueo.', 'Poco tiempo para vos.'].map((item, i) => <li key={i} className="flex items-center gap-3 text-[#6D4C41] opacity-70" data-id="element-152">
                  <div className="w-1.5 h-1.5 bg-[#8D6E63] rounded-full" data-id="element-153" />
                  <span data-id="element-154">{item}</span>
                </li>)}
            </ul>
          </div>

          <div className="bg-white p-10 rounded-[2rem] border border-[#E1BEE7]/30 shadow-xl shadow-[#E1BEE7]/10 relative overflow-hidden" data-id="element-155">
            <div className="absolute top-0 right-0 p-4" data-id="element-156">
              <Sparkles className="w-8 h-8 text-[#D4AF37] opacity-20" data-id="element-157" />
            </div>
            <h3 className="text-2xl font-bold mb-8 text-[#4E342E]" data-id="element-158">
              Lo que vas a trabajar con Volver a Mí:
            </h3>
            <ul className="space-y-4" data-id="element-159">
              {['Más calma interior.', 'Más conexión con vos misma.', 'Más claridad para cerrar ciclos.', 'Más amor propio.', 'Una rutina espiritual simple.', 'Una nueva forma de acompañarte emocionalmente.'].map((item, i) => <li key={i} className="flex items-center gap-3 text-[#4E342E] font-medium" data-id="element-160">
                  <Heart className="w-4 h-4 text-[#D81B60]" data-id="element-161" />
                  <span data-id="element-162">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <p className="text-center mt-12 text-sm text-[#8D6E63] italic" data-id="element-163">
          Este material es una guía de crecimiento personal y bienestar
          espiritual. Cada proceso es único y podés hacerlo a tu ritmo.
        </p>
      </Section>

      {/* QUÉ INCLUYE EL ACCESO */}
      <Section className="bg-[#F9F5F0]/30" data-id="element-164">
        <div className="text-center mb-24 md:mb-40 px-4" data-id="element-165">
          <h2 className="text-6xl md:text-8xl font-bold mb-12" data-id="element-166">
            Acceso completo a Volver a Mí
          </h2>
          <p className="text-3xl md:text-4xl text-[#6D4C41] max-w-6xl mx-auto font-light leading-relaxed" data-id="element-167">
            Al ingresar vas a recibir el ebook principal junto con 5 bonos
            creados para acompañarte en distintos momentos de tu proceso:
            soltar, agradecer, calmarte, armonizar tu energía y abrir una nueva
            etapa.
          </p>
        </div>

        <div className="bg-white p-8 md:p-24 lg:p-32 rounded-#[3rem] md:rounded-#[5rem] border border-#[E1BEE7]/20 shadow-2xl mb-24 overflow-visible max-w-1400px mx-auto" data-id="element-168">
          <div className="grid md:grid-cols-2 gap-16 lg:gap-32 items-center" data-id="element-169">
            <div className="order-2 md:order-1" data-id="element-170">
              <div className="inline-block px-6 py-2 bg-[#FCE4EC] text-[#D81B60] rounded-full text-sm font-bold uppercase tracking-[0.2em] mb-8" data-id="element-171">
                Producto Principal
              </div>
              <h3 className="text-5xl md:text-7xl font-bold mb-10 text-[#4E342E] leading-tight" data-id="element-172">
                EBOOK PRINCIPAL <br data-id="element-173" />{' '}
                <span className="text-[#D4AF37]" data-id="element-174">Volver a Mí</span>
              </h3>
              <p className="text-2xl md:text-3xl text-[#6D4C41] leading-relaxed mb-12 font-light" data-id="element-175">
                Una guía espiritual y emocional profunda para trabajar el amor
                propio, la paz interior, el Ho’oponopono, la gratitud y rituales
                sagrados para volver a conectar con tu esencia.
              </p>
              <div className="flex flex-wrap items-center gap-12 text-xl font-bold text-[#8D6E63] uppercase tracking-[0.3em]" data-id="element-176">
                <div className="flex items-center gap-5" data-id="element-177">
                  <Smartphone className="w-10 h-10 text-[#D4AF37]" data-id="element-178" /> Lectura
                  Digital
                </div>
                <div className="flex items-center gap-5" data-id="element-179">
                  <Download className="w-10 h-10 text-[#D4AF37]" data-id="element-180" /> Acceso
                  Inmediato
                </div>
              </div>
            </div>
            <div className="relative group" data-id="element-181">
              {/* Decorative background glow */}
              <div className="absolute -inset-4 bg-gradient-to-tr from-[#FCE4EC]/40 to-[#F3E5F5]/40 blur-2xl rounded-[2.5rem] -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-182" />

              <div className="relative rounded-#[2rem] md:rounded-#[3rem] overflow-hidden shadow-#[0_20px_50px_-12px_rgba(78,52,46,0.15)] border-4 border-white bg-white transition-transform duration-500 group-hover:scale-#[1.02]" data-id="element-183">
                <img src="https://cdn.magicpatterns.com/uploads/oMFhfLnDc7fQN71k7qyuQx/Volver_a_Mi.png" alt="Volver a Mí - Ebook Principal" className="w-full h-auto object-cover" data-id="element-184" />

                {/* Subtle overlay shine */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-transparent pointer-events-none" data-id="element-185" />
              </div>

              {/* Floating badge for premium feel */}
              <div className="absolute -bottom-4 -right-4 bg-white px-6 py-3 rounded-2xl shadow-xl border border-[#E1BEE7]/20 hidden md:flex items-center gap-2 animate-float-slow" data-id="element-186">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" data-id="element-187" />
                <span className="text-xs font-bold text-[#4E342E] uppercase tracking-widest" data-id="element-188">
                  Calidad Premium
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mb-12" data-id="element-189">
          <h3 className="md:text-3xl font-bold text-[28px]" data-id="element-190">
            Además, recibís 5 bonos de transformación interior
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12" data-id="element-191">
          {[{
          title: 'BONO #1: Ritual de Liberación Emocional',
          text: 'Cartas, pérdida y cierre de ciclos para soltar vínculos, recuerdos y cargas que todavía duelen.',
          pain: 'Para esos momentos en los que sentís que hay algo del pasado que todavía pesa y necesitás liberarlo desde el amor.',
          image: 'https://cdn.magicpatterns.com/uploads/ewHfonHKzafEitYQcEProh/bono_1.png'
        }, {
          title: 'BONO #2: Diario de Gratitud y Manifestación',
          text: 'Un espacio íntimo para agradecer, ordenar tus emociones y abrirte a una nueva etapa.',
          pain: 'Para transformar tu energía diaria, enfocar tu mente en lo que querés crear y conectar con una versión más consciente.',
          image: 'https://cdn.magicpatterns.com/uploads/vUtSaM38i88nu2nPBmhtyt/bono_2.png'
        }, {
          title: 'BONO #3: Chakras en Armonía',
          text: 'Una guía simple para equilibrar tu energía, desbloquear emociones y reconectar con tu bienestar interior.',
          pain: 'Para cuando sentís que tu energía está pesada, bloqueada o desordenada y querés volver a sentirte en equilibrio.',
          image: 'https://cdn.magicpatterns.com/uploads/cAPgVyrdr6v9SGy9zRmvJK/bono_3.png'
        }, {
          title: 'BONO #4: Mantras y Calma Mental',
          text: 'Frases, afirmaciones y audios para calmar tu mente, elevar tu energía y volver a tu centro.',
          pain: 'Para esos momentos donde la mente no para y necesitás una herramienta simple para volver a la calma.',
          image: 'https://cdn.magicpatterns.com/uploads/qAUFf6xXMMRFa2qbQD1QyK/bono_4.png'
        }, {
          title: 'BONO #5: Ceremonia de Renacimiento',
          text: 'Rituales holísticos para cerrar etapas, agradecer lo vivido y abrir un nuevo ciclo desde el amor propio.',
          pain: 'Para mujeres que sienten que llegó el momento de cerrar una etapa y comenzar otra desde una energía más liviana.',
          image: 'https://cdn.magicpatterns.com/uploads/vxE45tf9u9d8HEPnFuGjKT/bono_5.png'
        }].map((bono, i) => <motion.div key={i} whileHover={{
          y: -10,
          scale: 1.02
        }} className="bg-white p-8 md:p-10 rounded-[3rem] border border-[#E1BEE7]/30 shadow-xl flex flex-col h-full overflow-visible transition-all duration-500" data-id="element-192">
              <div className="mb-8 relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-[#F9F5F0]" data-id="element-193">
                <img src={bono.image} alt={bono.title} className="w-full h-auto object-cover aspect-[4/5] md:aspect-square lg:aspect-[4/5]" data-id="element-194" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#4E342E]/10 to-transparent pointer-events-none" data-id="element-195" />
              </div>
              <h4 className="text-2xl md:text-3xl font-bold mb-4 text-[#4E342E] leading-tight" data-id="element-196">
                {bono.title}
              </h4>
              <p className="text-lg text-[#6D4C41] mb-8 flex-grow font-light leading-relaxed" data-id="element-197">
                {bono.text}
              </p>
              <div className="bg-[#F9F5F0] p-6 rounded-[2rem] text-sm italic text-[#8D6E63] border border-[#E1BEE7]/10" data-id="element-198">
                <span className="font-bold block mb-2 not-italic text-[#4E342E] uppercase tracking-widest text-xs" data-id="element-199">
                  Ideal para:
                </span>
                {bono.pain}
              </div>
            </motion.div>)}
        </div>
      </Section>

      {/* CARRUSEL DE CARTAS HO'OPONOPONO */}
      <HooponoponoCarousel data-id="element-200" />

      {/* MÉTODO EN 4 PASOS */}
      <Section data-id="element-201">
        <div className="text-center mb-16" data-id="element-202">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-203">
            Un camino simple para volver a vos
          </h2>
          <div className="w-24 h-1 bg-[#E1BEE7] mx-auto rounded-full" data-id="element-204" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-205">
          {[{
          step: 'Paso 1',
          title: 'Soltar',
          desc: 'Liberar emociones, recuerdos y vínculos que ya no querés seguir cargando.',
          icon: Wind
        }, {
          step: 'Paso 2',
          title: 'Calmar',
          desc: 'Usar respiraciones, mantras y palabras gatillo para volver a tu centro.',
          icon: Moon
        }, {
          step: 'Paso 3',
          title: 'Reconectar',
          desc: 'Fortalecer tu amor propio, tu gratitud y tu conexión espiritual.',
          icon: Heart
        }, {
          step: 'Paso 4',
          title: 'Renacer',
          desc: 'Cerrar ciclos y abrir una nueva etapa desde una energía más liviana.',
          icon: Sun
        }].map((item, i) => <div key={i} className="text-center flex flex-col items-center" data-id="element-206">
              <div className="w-16 h-16 bg-[#FDF7FF] rounded-full flex items-center justify-center mb-6 border border-[#E1BEE7]/30 shadow-sm" data-id="element-207">
                <item.icon className="w-8 h-8 text-[#8D6E63]" data-id="element-208" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#D4AF37] mb-2" data-id="element-209">
                {item.step}
              </span>
              <h4 className="text-xl font-bold mb-3 text-[#4E342E]" data-id="element-210">
                {item.title}
              </h4>
              <p className="text-[#6D4C41] text-sm leading-relaxed" data-id="element-211">
                {item.desc}
              </p>
            </div>)}
        </div>
      </Section>

      {/* BLOQUE EMOCIONAL DE CONFIANZA */}
      <Section className="bg-[#FCE4EC]/20 relative overflow-hidden" data-id="element-212">
        <div className="absolute top-0 left-0 p-10 opacity-10" data-id="element-213">
          <Flower2 className="w-32 h-32" data-id="element-214" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-215">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-216">
            No tenés que hacerlo perfecto. Solo tenés que empezar.
          </h2>
          <div className="space-y-6 text-lg text-[#6D4C41] font-light leading-relaxed" data-id="element-217">
            <p data-id="element-218">
              Volver a Mí no fue creado para exigirte más. Fue creado para
              acompañarte.
            </p>
            <p data-id="element-219">
              Podés leerlo a tu ritmo, repetir los ejercicios cuando lo
              necesites y usar los bonos según el momento que estés atravesando:
              soltar, agradecer, calmarte, armonizar tu energía o comenzar una
              nueva etapa.
            </p>
            <p className="font-medium text-[#4E342E]" data-id="element-220">
              Es un espacio para vos. Para respirar. Para escribir. Para sanar
              desde adentro. Para volver a elegirte.
            </p>
          </div>
        </div>
      </Section>

      {/* BLOQUE DE OFERTA */}
      <Section id="oferta" className="bg-white" data-id="element-221">
        <div className="max-w-4xl mx-auto bg-[#F9F5F0] rounded-[3rem] p-8 md:p-16 border border-[#E1BEE7]/30 shadow-xl" data-id="element-222">
          <div className="text-center mb-12" data-id="element-223">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#4E342E]" data-id="element-224">
              Hoy accedés a todo esto
            </h2>
            <p className="text-[#8D6E63]" data-id="element-225">
              Tu kit completo de sanación y renacimiento
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-12" data-id="element-226">
            <ul className="space-y-4" data-id="element-227">
              {['Ebook principal Volver a Mí.', 'Bono #1: Ritual de Liberación Emocional.', 'Bono #2: Diario de Gratitud y Manifestación.', 'Bono #3: Chakras en Armonía.', 'Bono #4: Mantras y Calma Mental.', 'Bono #5: Ceremonia de Renacimiento.', 'Material digital en PDF.', 'Audios y enlaces incluidos.', 'Acceso inmediato.', 'Lectura desde cualquier dispositivo.'].map((item, i) => <li key={i} className="flex items-center gap-3 text-[#5D4037]" data-id="element-228">
                  <Check className="w-5 h-5 text-[#D81B60] shrink-0" data-id="element-229" />
                  <span data-id="element-230">{item}</span>
                </li>)}
            </ul>

            <div className="flex flex-col items-center justify-center text-center p-1 md:p-4 bg-white rounded-[2.5rem] border border-[#E1BEE7]/20 overflow-hidden shadow-sm" data-id="element-231">
              <div className="w-full mb-4 relative group" data-id="element-232">
                {/* Glow decorativo sutil */}
                <div className="absolute -inset-8 bg-gradient-to-b from-[#FCE4EC]/40 to-transparent blur-3xl rounded-full -z-10 opacity-60" data-id="element-233" />

                <img src="https://cdn.magicpatterns.com/uploads/7XCa3LQfFNwHfPumiVGTZD/ChatGPT_Image_12_jun_2026,_20_08_00.webp" alt="Volver a Mí - Ebook y Bonos Pack Completo" className="w-full h-auto object-contain rounded-[2rem] transition-transform duration-1000 group-hover:scale-[1.02]" loading="lazy" data-id="element-234" />
              </div>

              <div className="px-1 md:px-8 w-full pb-8" data-id="element-235">
                <div className="mb-6" data-id="element-236">
                  <span className="text-[#8D6E63] text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold block mb-4 opacity-80" data-id="element-237">
                    ACCESO TOTAL INMEDIATO
                  </span>

                  <div className="flex flex-col items-center justify-center gap-1" data-id="element-238">
                    <span className="text-[#8D6E63] line-through opacity-40 text-sm md:text-base font-light" data-id="element-239">
                      antes $39.990
                    </span>
                    <div className="flex items-baseline gap-1" data-id="element-240">
                      <span className="text-xs md:text-sm font-medium text-[#4E342E] mb-1" data-id="element-241">
                        ahora
                      </span>
                      <span className="text-3xl md:text-4xl font-extrabold text-[#4E342E] tracking-tight" data-id="element-242">
                        $19.990
                      </span>
                    </div>
                  </div>
                </div>

                <Button onClick={() => window.location.href = 'https://sanacion.shop/cart/43340694388784:1'} className="w-full text-lg md:text-xl py-5 md:py-6 shadow-xl shadow-[#E1BEE7]/30 bg-[#E1BEE7] hover:bg-[#D1A3D1] text-[#4E342E] font-bold tracking-wide" data-id="element-243">
                  Sí, quiero volver a mí
                </Button>

                {/* Sección de Confianza - Máximo Protagonismo Mobile */}
                <div className="mt-10 pt-10 border-t border-[#F9F5F0] w-full" data-id="element-244">
                  <div className="flex flex-col items-center text-center" data-id="element-245">
                    <p className="text-[11px] md:text-xs font-bold text-[#8D6E63] uppercase tracking-[0.3em] mb-10 opacity-90" data-id="element-246">
                      COMPRA SEGURA CON ACCESO INMEDIATO
                    </p>

                    <div className="space-y-12 w-full max-w-full px-0" data-id="element-247">
                      {/* Métodos de Pago - Ocupa casi todo el ancho (96%) */}
                      <div className="flex justify-center w-[96%] md:w-[92%] mx-auto" data-id="element-248">
                        <img src="https://cdn.magicpatterns.com/uploads/axgM6oMuXe8Kds4834wcSp/Helado-Artesanal-1.webp" alt="Pago Seguro Garantizado" className="w-full h-auto object-contain" loading="lazy" data-id="element-249" />
                      </div>

                      {/* Grilla de Sellos - 2 Columnas Amplias con mayor tamaño */}
                      <div className="grid grid-cols-2 gap-x-2 gap-y-12 items-center justify-items-center w-[96%] md:w-[92%] mx-auto" data-id="element-250">
                        <img src="https://cdn.magicpatterns.com/uploads/wAC9ezKqHUTh2Qpu1v91YQ/pago_mercado_libre.jpg" alt="Mercado Pago" className="h-16 md:h-20 w-auto object-contain" loading="lazy" data-id="element-251" />
                        <img src="https://cdn.magicpatterns.com/uploads/7mzh54DswX6pz8mJmAq765/safe-purchase-7779878_1280.png" alt="Compra Segura" className="h-20 md:h-24 w-auto object-contain" loading="lazy" data-id="element-252" />
                        <img src="https://cdn.magicpatterns.com/uploads/1EUcaa8i9vs484pdpGRdyh/zn9vtwfya.png" alt="Garantía 7 días" className="h-44 md:h-52 w-auto object-contain" loading="lazy" data-id="element-253" />
                        <img src="https://cdn.magicpatterns.com/uploads/k3zoxZBdrQLeNkoyffkFbM/MercadoLider-en-Mercado-Libre.webp" alt="MercadoLíder Platinum" className="h-32 md:h-40 w-auto object-contain opacity-100" loading="lazy" data-id="element-254" />
                      </div>
                    </div>

                    <p className="mt-12 text-[11px] md:text-xs text-[#8D6E63] leading-relaxed max-w-[320px] mx-auto opacity-80 italic px-4" data-id="element-255">
                      Tu pago se procesa de forma segura y recibís el acceso
                      digital al material inmediatamente.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* MIRÁ CÓMO ES POR DENTRO */}
      <Section className="bg-[#FDF7FF]/30" data-id="element-256">
        <div className="text-center mb-24 md:mb-48 px-4 max-w-[1600px] mx-auto" data-id="element-257">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-258">
            <h2 className="md:text-9xl font-bold mb-12 text-[#4E342E] tracking-tight text-[48px]" data-id="element-259">
              Mirá cómo es por dentro
            </h2>
            <p className="md:text-5xl text-[#6D4C41] max-w-7xl mx-auto font-light opacity-80 text-[24px]" data-id="element-260">
              Al ingresar vas a encontrar todo organizado en un acceso simple,
              con el ebook principal y los bonos listos para leer, descargar y
              usar a tu ritmo.
            </p>
          </motion.div>
        </div>

        {/* Main Mockup Preview */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-24 md:mb-40 px-0 md:px-12 lg:px-24 w-full flex flex-col items-center" data-id="element-261">
          <div className="relative w-full max-w-[1200px] mx-auto rounded-none sm:rounded-2xl md:rounded-[3rem] overflow-hidden shadow-[0_30px_80px_-15px_rgba(78,52,46,0.15)] border-y-4 border-x-0 sm:border-4 md:border-8 border-white bg-[#F9F5F0]" data-id="element-262">
            <video controls preload="metadata" className="w-full h-auto block" data-id="element-263">
              <source src="https://cdn.magicpatterns.com/uploads/my9Z9C5XiPMMiCFrrjwM7m/video%20para%20web.mp4" type="video/mp4" data-id="element-264" />
              Tu navegador no soporta la reproducción de videos.
            </video>
            <div className="absolute inset-0 bg-gradient-to-tr from-[#FCE4EC]/5 via-transparent to-white/10 pointer-events-none" data-id="element-265" />
          </div>
          <p className="text-center mt-8 text-[#6D4C41] font-light text-[15px] md:text-lg max-w-3xl mx-auto px-6 leading-relaxed" data-id="element-266">
            “Recorré una parte del acceso y descubrí cómo están organizados el
            ebook principal, los bonos, las prácticas y los recursos que vas a
            recibir.”
          </p>
        </motion.div>

        {/* Grid of Content Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-24 lg:gap-32 mb-32 max-w-1600px mx-auto px-6" data-id="element-267">
          {/* Ebook Principal */}

          {/* Bonos */}
          {[{
          title: 'Bono #2: Diario de Gratitud y Manifestación',
          text: 'Un espacio íntimo para agradecer, ordenar tus emociones y abrirte a una nueva etapa.',
          image: 'https://cdn.magicpatterns.com/uploads/vUtSaM38i88nu2nPBmhtyt/bono_2.png'
        }].map((bono, i) => {})}
        </div>

        {/* Trust Block */}
        <div className="max-w-1400px mx-auto bg-white p-12 md:p-32 rounded-#[3rem] md:rounded-#[7rem] border border-#[E1BEE7]/20 shadow-2xl relative overflow-visible" data-id="element-268">
          <div className="absolute top-0 right-0 p-8 opacity-5" data-id="element-269">
            <ShieldCheck className="w-32 h-32 text-[#D4AF37]" data-id="element-270" />
          </div>

          <div className="grid md:grid-cols-2 gap-12 lg:gap-40 items-center relative z-10" data-id="element-271">
            <div data-id="element-272">
              <h3 className="md:text-6xl font-bold mb-12 text-[#4E342E] text-[32px]" data-id="element-273">
                Todo en un solo lugar, simple y fácil de usar
              </h3>
              <p className="text-[#6D4C41] mb-16 font-light text-[20px]" data-id="element-274">
                No tenés que buscar por todos lados ni sentirte perdida. El
                acceso está pensado para que puedas elegir el material que
                necesitás en ese momento y avanzar a tu ritmo: soltar,
                agradecer, calmarte, armonizar tu energía o comenzar una nueva
                etapa.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-10" data-id="element-275">
              {[{
              icon: Smartphone,
              text: 'Acceso inmediato.'
            }, {
              icon: Download,
              text: 'Material digital en PDF.'
            }, {
              icon: Check,
              text: 'Lectura desde celular, tablet o computadora.'
            }, {
              icon: Sparkles,
              text: 'Bonos organizados por tema.'
            }, {
              icon: Heart,
              text: 'Recursos para usar a tu ritmo.'
            }, {
              icon: Wind,
              text: 'Audios y enlaces de escucha incluidos.'
            }].map((item, i) => <div key={i} className="flex items-center gap-8 text-[#5D4037]" data-id="element-276">
                  <div className="w-14 h-14 bg-[#F9F5F0] rounded-full flex items-center justify-center shrink-0 shadow-lg" data-id="element-277">
                    <item.icon className="w-7 h-7 text-[#D4AF37]" data-id="element-278" />
                  </div>
                  <span className="font-medium text-[16px]" data-id="element-279">{item.text}</span>
                </div>)}
            </div>
          </div>
        </div>

        <div className="text-center mt-32 md:mt-56" data-id="element-280">
          <Button onClick={() => window.location.href = 'https://sanacion.shop/cart/43340694388784:1'} className="w-full md:w-auto px-32 py-10 text-5xl shadow-#[0_25px_60px_-15px_rgba(225,190,231,0.5)]" data-id="element-281">
            Quiero acceder ahora
          </Button>
        </div>
      </Section>

      {/* BLOQUE DE VALOR PERCIBIDO */}
      <Section className="bg-[#FDF7FF]/50" data-id="element-282">
        <div className="max-w-3xl mx-auto text-center" data-id="element-283">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-284">
            Más que un ebook, una experiencia para volver a vos
          </h2>
          <p className="text-lg text-[#6D4C41] leading-relaxed font-light" data-id="element-285">
            Cada parte de este acceso fue organizada para acompañarte en un
            momento específico de tu proceso interior. Cuando necesites soltar,
            vas al Ritual de Liberación Emocional. Cuando necesites ordenar tus
            emociones, usás el Diario de Gratitud. Cuando quieras armonizar tu
            energía, abrís Chakras en Armonía. Cuando tu mente necesite calma,
            usás los mantras y palabras gatillo. Cuando sientas que es momento
            de empezar de nuevo, hacés tu Ceremonia de Renacimiento Personal.
          </p>
        </div>
      </Section>

      {/* FAQ */}
      <Section data-id="element-286">
        <div className="max-w-3xl mx-auto" data-id="element-287">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-288">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-2" data-id="element-289">
            <FAQItem question="¿Es un curso?" answer="No. Es una guía digital en PDF con bonos complementarios y recursos de acompañamiento espiritual y emocional." data-id="element-290" />
            <FAQItem question="¿Necesito experiencia previa?" answer="No. Está pensado para mujeres que desean empezar de forma simple, amorosa y práctica." data-id="element-291" />
            <FAQItem question="¿Cómo recibo el acceso?" answer="Recibís el acceso digital para descargar o leer el material desde tu dispositivo inmediatamente después de tu compra." data-id="element-292" />
            <FAQItem question="¿Incluye bonos?" answer="Sí. Incluye el ebook principal Volver a Mí y los 5 bonos de transformación interior mencionados anteriormente." data-id="element-293" />
            <FAQItem question="¿Puedo verlo desde el celular?" answer="Sí. Podés acceder al material desde tu celular, tablet o computadora." data-id="element-294" />
            <FAQItem question="¿Esto reemplaza terapia o ayuda profesional?" answer="No. Este material es una guía de crecimiento personal, bienestar espiritual y acompañamiento emocional. Si necesitas ayuda profesional, siempre es importante buscar apoyo adecuado." data-id="element-295" />
          </div>
        </div>
      </Section>

      {/* TESTIMONIOS */}
      <Section className="bg-[#F9F5F0]/30" data-id="element-296">
        <div className="text-center mb-16" data-id="element-297">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-298">
            Lo que están sintiendo otras mujeres
          </h2>
          <p className="text-lg text-[#6D4C41] max-w-3xl mx-auto font-light leading-relaxed" data-id="element-299">
            Cada proceso es único, pero muchas mujeres coinciden en algo: cuando
            se regalan un momento para ellas, empiezan a sentirse más presentes,
            más livianas y más conectadas con su paz interior.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-300">
          {[{
          name: 'Mariana G.',
          location: 'Córdoba',
          comment: '“Empecé leyendo solo unas páginas por la noche y me sorprendió lo mucho que necesitaba ese momento para mí. Me ayudó a bajar un cambio, escribir lo que sentía y empezar a soltar cosas que venía guardando hace tiempo.”'
        }, {
          name: 'Lucía R.',
          location: 'Buenos Aires',
          comment: '«El bono de liberación emocional fue el que más me tocó. No lo hice apurada, lo fui haciendo a mi ritmo, pero sentí que por primera vez tenía una forma amorosa de cerrar una etapa sin pelearnos con lo que viví."'
        }, {
          name: 'Valeria M.',
          location: 'Mendoza',
          comment: '“Me gustó porque no es difícil de seguir. No necesitás saber nada previo. Es como una guía para volver a escucharte, respirar y ordenar un poco todo lo que una viene sintiendo por dentro.”'
        }, {
          name: 'Sofía L.',
          location: 'Santa Fe',
          comment: '“El diario de gratitud me ayudó mucho a empezar mis días con otra energía. No es magia, es sentarte un ratito con vos misma y darte cuenta de que también merecés calma.”'
        }, {
          name: 'Carolina P.',
          location: 'Rosario',
          comment: '«Lo compré porque sentí que necesitaba cerrar un ciclo. Me gustó mucho la forma en que estaba explicado, con palabras simples y ejercicios que se sienten íntimos, femeninos y muy acompañadores."'
        }, {
          name: 'Paula D.',
          location: 'Tucumán',
          comment: '“Uso los mantras cuando siento la mente muy cargada. Me gusta tener frases simples para repetir y volver a mi centro. Es un material muy lindo para tener a mano.”'
        }, {
          name: 'Natalia S.',
          location: 'Neuquén',
          comment: '“Lo que más me gustó fue que no te exige hacerlo perfecto. Lo vas haciendo a tu tiempo. Para mí fue como abrir un espacio personal que venía postergando hace mucho.”'
        }, {
          name: 'Romina A.',
          location: 'La Plata',
          comment: '“Me encantó la parte de renacimiento personal. Sentí que era justo lo que necesitaba para ordenar lo vivido y empezar una nueva etapa con más amor propio.”'
        }].map((testimonial, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white p-8 rounded-[2rem] border border-[#E1BEE7]/20 shadow-sm flex flex-col relative overflow-hidden group" data-id="element-301">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-302">
                <Star className="w-8 h-8 text-[#D4AF37] fill-[#D4AF37]" data-id="element-303" />
              </div>
              <div className="mb-6" data-id="element-304">
                <Sparkles className="w-5 h-5 text-[#D4AF37] mb-4" data-id="element-305" />
                <p className="text-[#6D4C41] italic leading-relaxed font-light" data-id="element-306">
                  {testimonial.comment}
                </p>
              </div>
              <div className="mt-auto pt-6 border-t border-[#F9F5F0]" data-id="element-307">
                <p className="font-bold text-[#4E342E]" data-id="element-308">{testimonial.name}</p>
                <p className="text-xs text-[#8D6E63] uppercase tracking-widest" data-id="element-309">
                  {testimonial.location}
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* BLOQUE DEJAR EXPERIENCIA */}
        <div className="max-w-2xl mx-auto mb-20" data-id="element-310">
          <div className="bg-white/50 backdrop-blur-sm p-8 md:p-12 rounded-[3rem] border border-[#E1BEE7]/20 shadow-sm" data-id="element-311">
            <div className="text-center mb-8" data-id="element-312">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-[#4E342E]" data-id="element-313">
                ¿Querés dejar tu experiencia?
              </h3>
              <p className="text-[#6D4C41] font-light" data-id="element-314">
                Tu proceso también importa. Si querés, podés escribir unas
                palabras sobre lo que sentiste al comenzar este camino.
              </p>
            </div>

            {!isSubmitted ? <form onSubmit={handleExperienceSubmit} className="space-y-4" data-id="element-315">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-316">
                  <input type="text" placeholder="Nombre" value={experienceForm.name} onChange={e => setExperienceForm({
                ...experienceForm,
                name: e.target.value
              })} className="w-full px-6 py-4 rounded-2xl bg-white border border-#[E1BEE7]/20 focus:outline-none focus:border-#[E1BEE7] transition-colors text-#[4E342E]" required data-id="element-317" />
                  <input type="text" placeholder="Ciudad / Provincia" value={experienceForm.location} onChange={e => setExperienceForm({
                ...experienceForm,
                location: e.target.value
              })} className="w-full px-6 py-4 rounded-2xl bg-white border border-#[E1BEE7]/20 focus:outline-none focus:border-#[E1BEE7] transition-colors text-#[4E342E]" required data-id="element-318" />
                </div>
                <textarea placeholder="Tu experiencia" value={experienceForm.comment} onChange={e => setExperienceForm({
              ...experienceForm,
              comment: e.target.value
            })} rows={4} className="w-full px-6 py-4 rounded-2xl bg-white border border-#[E1BEE7]/20 focus:outline-none focus:border-#[E1BEE7] transition-colors text-#[4E342E] resize-none" required data-id="element-319" />
                <Button className="w-full py-4 text-lg" data-id="element-320">
                  Compartir mi experiencia
                </Button>
              </form> : <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-center" data-id="element-321">
                <div className="bg-[#FDF7FF] p-6 rounded-2xl border border-[#E1BEE7]/20 mb-8" data-id="element-322">
                  <p className="text-[#4E342E] font-medium mb-2" data-id="element-323">
                    Gracias por compartir tu experiencia.
                  </p>
                  <p className="text-sm text-[#6D4C41] font-light" data-id="element-324">
                    Tu mensaje fue recibido como una vista previa privada para
                    acompañar tu proceso.
                  </p>
                </div>

                {previewData && <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="bg-white p-8 rounded-[2rem] border border-[#E1BEE7]/20 shadow-md text-left relative overflow-hidden mb-6" data-id="element-325">
                    <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-326">
                      <Star className="w-8 h-8 text-[#D4AF37] fill-[#D4AF37]" data-id="element-327" />
                    </div>
                    <div className="mb-6" data-id="element-328">
                      <Sparkles className="w-5 h-5 text-[#D4AF37] mb-4" data-id="element-329" />
                      <p className="text-[#6D4C41] italic leading-relaxed font-light" data-id="element-330">
                        “{previewData.comment}”
                      </p>
                    </div>
                    <div className="mt-auto pt-6 border-t border-[#F9F5F0]" data-id="element-331">
                      <p className="font-bold text-[#4E342E]" data-id="element-332">
                        {previewData.name}
                      </p>
                      <p className="text-xs text-[#8D6E63] uppercase tracking-widest" data-id="element-333">
                        {previewData.location}
                      </p>
                    </div>
                  </motion.div>}

                <p className="text-[10px] text-[#8D6E63] italic" data-id="element-334">
                  Por ahora, los comentarios escritos en este espacio se
                  muestran solo como vista previa y no se publican
                  automáticamente.
                </p>
              </motion.div>}
          </div>
        </div>

        <div className="text-center" data-id="element-335">
          <p className="text-sm text-[#8D6E63] italic mb-10" data-id="element-336">
            “Experiencias compartidas por mujeres que eligieron regalarse un
            espacio de calma, amor propio y reconexión interior.”
          </p>
          <p className="text-sm text-[#6D4C41] font-light" data-id="element-337">
            Tu momento para volver a vos puede empezar hoy.
          </p>
        </div>
      </Section>

      {/* ÚLTIMO CTA */}
      <Section className="bg-[#4E342E] text-white text-center rounded-t-[4rem]" data-id="element-338">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-339">
          <Flower2 className="w-12 h-12 text-[#E1BEE7] mx-auto mb-8 opacity-50" data-id="element-340" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" data-id="element-341">
            Soltá lo que pesa y volvé a vos
          </h2>
          <p className="text-xl text-[#E1BEE7] mb-12 max-w-2xl mx-auto font-light" data-id="element-342">
            Si sentís que llegó el momento de cerrar ciclos, regalarte calma y
            volver a conectar con tu paz interior, este acceso fue creado para
            acompañarte.
          </p>

          <div className="mt-20 pt-8 border-t border-white/10 text-sm text-white/40 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-343">
            <p data-id="element-344">
              © {new Date().getFullYear()} Volver a Mí. Todos los derechos
              reservados.
            </p>
            <div className="flex gap-6" data-id="element-345">
              <span className="hover:text-white/60 cursor-pointer" data-id="element-346">
                Términos
              </span>
              <span className="hover:text-white/60 cursor-pointer" data-id="element-347">
                Privacidad
              </span>
            </div>
          </div>
        </motion.div>
      </Section>
    </div>;
}